/**
 * The Career Explorer display rules (docs/julius/AGU-Career-Explorer-API-2026-10-26.md:401-411), as pure functions the
 * pages share: a requirement CV evidence has not reached is "not yet evidenced", never "missing" and never "met"; only a
 * `cvBacked` row says "from your CV"; scores are 0..1 on the wire and become a percent only in `PercentLabel`; the `band`
 * (not the score) picks the badge.
 */

export const NOT_YET_EVIDENCED_LABEL = 'not yet evidenced';
const CV_BACKED_LABEL = 'from your CV';

export type CvFitBandName = 'ready' | 'near' | 'longer';
export type CvBadgeTone = 'ok' | 'near' | 'grey';

/** A run of readiness text; `strong` renders as `<b>` (never `[innerHTML]`). */
export interface CvTextSegment {
    readonly text: string;
    readonly strong: boolean;
}

export interface CvBandBadge {
    readonly Label: 'Ready now' | 'Near-ready' | 'Longer-term';
    readonly Tone: CvBadgeTone;
}

export function RequirementState(r: { evidenced: boolean }): 'met' | typeof NOT_YET_EVIDENCED_LABEL {
    return r.evidenced ? 'met' : NOT_YET_EVIDENCED_LABEL;
}

export function CvBackedMark(r: { cvBacked: boolean }): typeof CV_BACKED_LABEL | null {
    return r.cvBacked ? CV_BACKED_LABEL : null;
}

/** `round(overlap * 100)` + `%`, with `overlap` clamped to 0..1. Throws `RangeError` on NaN. */
export function PercentLabel(overlap: number): string {
    if (Number.isNaN(overlap)) throw new RangeError('PercentLabel: overlap is NaN');
    const clamped = Math.min(1, Math.max(0, overlap));
    return `${Math.round(clamped * 100)}%`;
}

export function BandBadge(band: CvFitBandName): CvBandBadge {
    switch (band) {
        case 'ready': return { Label: 'Ready now', Tone: 'ok' };
        case 'near': return { Label: 'Near-ready', Tone: 'near' };
        case 'longer': return { Label: 'Longer-term', Tone: 'grey' };
    }
}

/** P/06_career-explorer.html:1521-1530 minus the "with n confirmations" variants (spec Deviations). */
export function ReadinessLine(match: { band: CvFitBandName; rank: number }): readonly CvTextSegment[] {
    switch (match.band) {
        case 'ready':
            return match.rank === 1
                ? [{ text: 'Already your strongest fit', strong: false }]
                : [{ text: 'Ready now', strong: true }, { text: " on what you’ve confirmed", strong: false }];
        case 'near':
            return [{ text: 'Near-ready', strong: true }, { text: ' as you add skills', strong: false }];
        case 'longer':
            return [{ text: 'Longer-term', strong: true }, { text: ', needs skills not on your CV yet', strong: false }];
    }
}
